// @ts-nocheck
/**
 * カードコレクションシステム - プレイヤーデータ構造
 *
 * プレイヤーデータの構造定義と初期化
 */

import { ECONOMY } from './constants';
import { createInitialMarketState } from '../market/marketEngine';
import { createInitialAssetHistory } from '../systems/assetCalculator';
import { createInitialMerchantData } from '../merchant/merchantSystem';
import { createInitialTradeData } from '../merchant/tradeSystem';
import { EFFECT_LEVELS } from '../../styles/rarityEffects';
import { storage } from './storage';

// ========================================
// プレイヤーデータ取得
// ========================================

/**
 * プレイヤーデータを取得する便利関数
 * storageから読み込み、存在しない場合はnullを返す
 * @returns {Object|null} プレイヤーデータ
 */
export const getPlayerData = () => {
  return storage.load();
};

// ========================================
// 投資データ初期化
// ========================================

/**
 * 初期投資データを作成
 * @returns {Object} 初期投資データ
 */
export const createInitialInvestmentData = () => ({
  // ファンド保有
  holdings: [],
  // 例:
  // {
  //   id: 'holding_001',
  //   fundId: 'region_fire',
  //   units: 2,
  //   purchasePrice: 10000,
  //   purchaseDate: 15,       // 購入時の戦闘番号
  //   expiryDate: 45,         // 期限（30日後）
  //   leverage: 1,            // レバレッジ倍率（Step 4）
  //   direction: 'long',      // 'long' | 'short'（Step 4）
  //   stopLoss: null,         // ストップロス %（Step 5）
  //   takeProfit: null,       // テイクプロフィット %（Step 5）
  // }

  // FXポジション（Step 3）
  fxPositions: [],
  // 例:
  // {
  //   id: 'fx_001',
  //   pair: 'FIRE_WATER',
  //   direction: 'long',
  //   leverage: 1,
  //   margin: 10000,
  //   entryRate: 1.250,
  //   openedAt: 30,
  //   stopLoss: null,
  //   takeProfit: null,
  // }

  // 取引履歴
  history: [],
  // 例:
  // {
  //   date: 15,
  //   action: 'buy' | 'sell',
  //   type: 'fund' | 'fx',
  //   fundId or pair,
  //   units or margin,
  //   price or rate,
  //   fee: 200,
  //   pnl: 500,              // 売却時のみ
  // }

  // ファンド価格状態
  fundPrices: {},
  // 例: { 'region_fire': 10200, 'region_water': 9800 }

  // FXレート状態（Step 3）
  fxRates: {},
  // 例: { 'FIRE_WATER': 1.250, 'LIGHT_DARK': 1.286 }

  // FXレート履歴（Step 3）
  fxRateHistory: {},
  // 例: { 'FIRE_WATER': [1.200, 1.225, 1.250] }

  // 価格履歴（最新30件）- ファンド用
  priceHistory: {},
  // 例: { 'region_fire': [10000, 10100, 10200] }

  // 累計維持コスト
  totalMaintenancePaid: 0,

  // 投資経験（レバレッジ解放用）
  experienceCount: 0,

  // 借金状態（Step 6）
  debt: null,
  // {
  //   amount: 15000,
  //   originalAmount: 15000,
  //   interest: 0,
  //   deadline: 32,
  //   createdAt: 25,
  //   source: 'leverage',
  // }

  // 質屋在庫（Step 7）
  pawnShopInventory: [],

  // 通算成績
  totalStats: {
    totalInvestments: 0,
    totalProfitableTrades: 0,
    totalInvestedAmount: 0,
    totalPayout: 0,
    totalProfit: 0,
    totalFeesPaid: 0,
  },
});

// ========================================
// ツインキーデータ初期化
// ========================================

/**
 * 初期ツインキーデータを作成
 * @returns {Object} 初期ツインキーデータ
 */
export const createInitialTwinKeyData = () => ({
  gold: 0, // 端末内の探索G。共通Gとの交換は行わない。
  // 探索統計
  stats: {
    totalExpeditions: 0,        // 総探索回数
    successfulClears: 0,        // クリア回数
    totalFloorsCleared: 0,      // 総階層クリア数
    totalBattlesWon: 0,         // 総バトル勝利数
    totalBattlesLost: 0,        // 総バトル敗北数
    deepestFloor: 0,            // 最深階層到達
    totalGoldEarned: 0,         // ツインキーで獲得した総ゴールド
    totalItemsFound: 0,         // 獲得アイテム数
  },

  // 大陸別統計
  continentStats: {
    // 例: fire: { clears: 5, bestFloor: 10 }
  },

  // お気に入りパーティ
  favoriteParties: [],
  // 例:
  // {
  //   id: 'party_001',
  //   name: 'お気に入り1',
  //   key1: { cardId: 'C0000001', rarity: 'SR' },
  //   key2: { cardId: 'C0000002', rarity: 'R' },
  //   members: ['C0000003', 'C0000004', 'C0000005', 'C0000006', 'C0000007'],
  //   createdAt: Date.now(),
  // }

  // 装備インベントリ（Phase 2で実装）
  equipment: [],
  // 例:
  // {
  //   id: 'equip_001',
  //   type: 'weapon',
  //   baseId: 'WPN_FLAME_SWORD',
  //   rarity: 'SR',
  //   level: 5,
  //   awakening: 2,
  //   stats: { attack: 150, critRate: 0.1 },
  // }

  // 素材インベントリ（Phase 2 錬金システム用）
  materials: {},
  // 例: { 'MAT_FIRE_CRYSTAL': 10, 'MAT_DRAGON_SCALE': 5 }

  // 進行中のダンジョン（復帰用）
  inProgressDungeon: null,
  // {
  //   dungeonId: 'dungeon_xxx',
  //   currentFloor: 3,
  //   party: { ... },
  //   savedAt: Date.now(),
  // }

  // 最後のプレイ
  lastPlayedAt: null,
});

// ========================================
// 大会データ初期化
// ========================================

/**
 * 初期大会データを作成
 * @returns {Object} 初期大会データ
 */
export const createInitialTournamentData = () => ({
  // NPC戦績
  competitorStats: {},

  // 現在開催中の大会
  currentTournament: null,

  // 現在の賭け
  currentBets: [],

  // 大会履歴（最新30件）
  history: [],

  // 次回大会スケジュール
  schedule: {
    nextDaily: 1,
    nextWeekly: 7,
    nextMajor: 21,
  },

  // 通算成績
  totalStats: {
    totalBets: 0,
    totalWins: 0,
    totalBetAmount: 0,
    totalPayout: 0,
    totalProfit: 0,
  },

  // 購入済みデッキ情報
  purchasedInfo: {},
});

// ========================================
// 設定のデフォルト値
// ========================================

export const DEFAULT_SETTINGS = {
  // レアリティエフェクト: 'full' | 'minimal' | 'off'
  rarityEffectLevel: EFFECT_LEVELS.FULL,
  // 振動フィードバック: true | false
  vibrationEnabled: true,
};

// ========================================
// スターターデッキ定義
// ========================================

/**
 * 新規プレイヤーに付与するスターターデッキのカードIDリスト
 * これらのカードはすべてC（コモン）版として付与される
 *
 * スターターデッキ「紅蓮の咆哮」- 炎ドラゴン入門（40枚）
 * ドラゴンと岩狸のシンプルなシナジーを学べる初心者向け構成
 */
export const STARTER_DECK_CARDS = [
  // ================================================================================
  // モンスター（26枚）
  // ================================================================================

  // 【コスト1】バニラ・サーチ役（6枚）
  'C0000292', 'C0000292', 'C0000292', // 炎のソルジャー x3 ATK500/HP500 【効果なし】
  'C0000163', 'C0000163', 'C0000163', // 岩狸・石ころ丸 x3 ATK400/HP400 ★サーチ

  // 【コスト2】低コストアタッカー（11枚）
  'C0000293', 'C0000293', 'C0000293', // 炎のソルジャーII x3 ATK900/HP1000 【効果なし】
  'C0000025', 'C0000025', 'C0000025', // ブレイズ・ドラゴン x3 ATK1200/HP1200 ★破壊時サーチ
  'C0000305', 'C0000305', 'C0000305', // フレア・リザード x3 ATK500/HP500 【変幻身】
  'C0000164', 'C0000164',             // 岩狸・岩太 x2 ATK800/HP1000 ★自壊時200ダメージ

  // 【コスト3】主力モンスター（6枚）
  'C0000021', 'C0000021',             // フレア・ドラゴン x2 ATK1800/HP1500 ★召喚時300バーン
  'C0000029', 'C0000029',             // クリムゾン・ワイバーン x2 ATK1400/HP1400 ★ドラゴン時ATK+400
  'C0000026', 'C0000026',             // インフェルノ・ドラゴン x2 ATK1600/HP1800 ★攻撃時ATK-300

  // 【コスト4】中堅モンスター（3枚）
  'C0000027', 'C0000027',             // マグマ・ドラゴン x2 ATK2000/HP2400 ★魔法使用時200ダメージ
  'C0000030',                          // バースト・ドラゴン・ナイト x1 ATK1800/HP2000 ★ドラゴン撃破で追加攻撃

  // ================================================================================
  // 魔法（11枚）
  // ================================================================================

  // 【コスト1】軽量魔法（2枚）
  'C0000033', 'C0000033',             // 紅蓮の覚醒 x2 ★ドラゴン覚醒+ATK700

  // 【コスト2】基本魔法（5枚）
  'C0000031', 'C0000031',             // 炎の咆哮 x2 ★ドラゴンATK+500 + 300ダメージ
  'C0000022', 'C0000022', 'C0000022', // 火竜の吐息 x3 ★1000ダメージ【刹那詠唱】

  // 【コスト3】中堅魔法（4枚）
  'C0000034', 'C0000034',             // 炎の嵐 x2 ★ドラゴン数×200ダメージ
  'C0000032', 'C0000032',             // ドラゴンの炎息 x2 ★800+全体300【刹那詠唱】

  // ================================================================================
  // フィールド（3枚）
  // ================================================================================
  'C0000037', 'C0000037',             // ドラゴンの火山 x2 ★ドラゴンATK+400 + 毎ターン300ダメージ
  'C0000187',                          // 狸の熔岩郷 x1 ★毎ターン岩狸蘇生 + 300回復
];

// ========================================
// データ構造
// ========================================

/**
 * 初期プレイヤーデータを作成
 * 注意: スターターカードはサーバー側で配布されるため、ここでは空のコレクションで開始
 * @returns {Object} 初期プレイヤーデータ
 */
export const createInitialPlayerData = () => {
  return {
    // 通貨
    gold: ECONOMY.INITIAL_GOLD,

    // 未開封パック数
    unopenedPacks: 0,

    // 所持カード（サーバーから同期される）
    collection: [],

    // ユーザー作成デッキ
    userDecks: [],

    // 動的市場状態
    market: createInitialMarketState(),

    // 資産履歴
    assetHistory: createInitialAssetHistory(),

    // 商人システム
    merchantData: createInitialMerchantData(),

    // トレードシステム
    tradeData: createInitialTradeData(),

    // 大会システム
    tournamentData: createInitialTournamentData(),

    // 投資システム
    investmentData: createInitialInvestmentData(),

    // ツインキーシステム
    twinKeyData: createInitialTwinKeyData(),

    // ユーザー設定
    settings: { ...DEFAULT_SETTINGS },

    // 統計
    stats: {
      totalBattles: 0,
      wins: 0,
      losses: 0,
      packsOpened: 0,
      totalGoldEarned: 0,
      totalGoldSpent: 0,
    },

    // メタデータ
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
};

// ========================================
// バリデーション
// ========================================

/**
 * プレイヤーデータの整合性を検証
 * @param {Object} data - プレイヤーデータ
 * @returns {boolean} 有効かどうか
 */
export const validatePlayerData = (data) => {
  if (!data || typeof data !== 'object') return { valid: false };
  if (typeof data.gold !== 'number' || data.gold < 0) return { valid: false };
  if (!Array.isArray(data.collection)) return { valid: false };
  if (!Array.isArray(data.userDecks)) return { valid: false };
  if (!data.stats || typeof data.stats !== 'object') return { valid: false };
  // unopenedPacksが存在しない場合は修復が必要
  if (typeof data.unopenedPacks !== 'number') return { valid: false, needsRepair: true };
  // marketが存在しない、または不完全な場合は修復が必要
  if (!data.market || typeof data.market !== 'object') return { valid: false, needsRepair: true };
  if (!data.market.priceHistory || typeof data.market.priceHistory !== 'object') return { valid: false, needsRepair: true };
  // assetHistoryが存在しない場合は修復が必要
  if (!data.assetHistory || typeof data.assetHistory !== 'object') return { valid: false, needsRepair: true };
  // merchantDataが存在しない場合は修復が必要
  if (!data.merchantData || typeof data.merchantData !== 'object') return { valid: false, needsRepair: true };
  // settingsが存在しない場合は修復が必要
  if (!data.settings || typeof data.settings !== 'object') return { valid: false, needsRepair: true };
  // tournamentDataが存在しない場合は修復が必要
  if (!data.tournamentData || typeof data.tournamentData !== 'object') return { valid: false, needsRepair: true };
  // investmentDataが存在しない場合は修復が必要
  if (!data.investmentData || typeof data.investmentData !== 'object') return { valid: false, needsRepair: true };
  // twinKeyDataが存在しない場合は修復が必要
  if (!data.twinKeyData || typeof data.twinKeyData !== 'object') return { valid: false, needsRepair: true };
  // tradeDataが存在しない場合は修復が必要
  if (!data.tradeData || typeof data.tradeData !== 'object') return { valid: false, needsRepair: true };
  return { valid: true };
};

/**
 * プレイヤーデータを修復（不足フィールドを補完）
 * @param {Object} data - プレイヤーデータ
 * @returns {Object} 修復されたデータ
 */
export const repairPlayerData = (data) => {
  const defaults = createInitialPlayerData([]);

  // marketの修復（priceHistoryが欠けている場合も対応）
  let repairedMarket;
  if (!data.market || typeof data.market !== 'object') {
    repairedMarket = createInitialMarketState();
  } else {
    // 既存のmarketがあるが、priceHistoryが欠けている場合
    const defaultMarket = createInitialMarketState();
    repairedMarket = {
      ...defaultMarket,
      ...data.market,
      priceHistory: data.market.priceHistory || defaultMarket.priceHistory,
    };
  }

  // assetHistoryの修復
  let repairedAssetHistory;
  if (!data.assetHistory || typeof data.assetHistory !== 'object') {
    repairedAssetHistory = createInitialAssetHistory();
  } else {
    repairedAssetHistory = {
      history: Array.isArray(data.assetHistory.history) ? data.assetHistory.history : [],
    };
  }

  // merchantDataの修復
  let repairedMerchantData;
  if (!data.merchantData || typeof data.merchantData !== 'object') {
    repairedMerchantData = createInitialMerchantData();
  } else {
    const defaultMerchant = createInitialMerchantData();
    repairedMerchantData = {
      ...defaultMerchant,
      ...data.merchantData,
      favorability: data.merchantData.favorability || defaultMerchant.favorability,
      tickets: data.merchantData.tickets || defaultMerchant.tickets,
    };
  }

  // settingsの修復
  const repairedSettings = {
    ...DEFAULT_SETTINGS,
    ...(data.settings || {}),
  };

  // tournamentDataの修復
  let repairedTournamentData;
  if (!data.tournamentData || typeof data.tournamentData !== 'object') {
    repairedTournamentData = createInitialTournamentData();
  } else {
    const defaultTournament = createInitialTournamentData();
    repairedTournamentData = {
      ...defaultTournament,
      ...data.tournamentData,
      competitorStats: data.tournamentData.competitorStats || {},
      currentBets: Array.isArray(data.tournamentData.currentBets) ? data.tournamentData.currentBets : [],
      history: Array.isArray(data.tournamentData.history) ? data.tournamentData.history : [],
      schedule: data.tournamentData.schedule || defaultTournament.schedule,
      totalStats: { ...defaultTournament.totalStats, ...(data.tournamentData.totalStats || {}) },
    };
  }

  // investmentDataの修復
  let repairedInvestmentData;
  if (!data.investmentData || typeof data.investmentData !== 'object') {
    repairedInvestmentData = createInitialInvestmentData();
  } else {
    const defaultInvestment = createInitialInvestmentData();
    repairedInvestmentData = {
      ...defaultInvestment,
      ...data.investmentData,
      holdings: Array.isArray(data.investmentData.holdings) ? data.investmentData.holdings : [],
      fxPositions: Array.isArray(data.investmentData.fxPositions) ? data.investmentData.fxPositions : [],
      history: Array.isArray(data.investmentData.history) ? data.investmentData.history : [],
      fundPrices: data.investmentData.fundPrices || {},
      fxRates: data.investmentData.fxRates || {},
      priceHistory: data.investmentData.priceHistory || {},
      totalMaintenancePaid: data.investmentData.totalMaintenancePaid || 0,
      experienceCount: data.investmentData.experienceCount || 0,
      debt: data.investmentData.debt || null,
      pawnShopInventory: Array.isArray(data.investmentData.pawnShopInventory) ? data.investmentData.pawnShopInventory : [],
      totalStats: { ...defaultInvestment.totalStats, ...(data.investmentData.totalStats || {}) },
    };
  }

  // twinKeyDataの修復
  let repairedTwinKeyData;
  if (!data.twinKeyData || typeof data.twinKeyData !== 'object') {
    repairedTwinKeyData = createInitialTwinKeyData();
  } else {
    const defaultTwinKey = createInitialTwinKeyData();
    repairedTwinKeyData = {
      ...defaultTwinKey,
      ...data.twinKeyData,
      stats: { ...defaultTwinKey.stats, ...(data.twinKeyData.stats || {}) },
      continentStats: data.twinKeyData.continentStats || {},
      favoriteParties: Array.isArray(data.twinKeyData.favoriteParties) ? data.twinKeyData.favoriteParties : [],
      equipment: Array.isArray(data.twinKeyData.equipment) ? data.twinKeyData.equipment : [],
      materials: data.twinKeyData.materials || {},
      inProgressDungeon: data.twinKeyData.inProgressDungeon || null,
      lastPlayedAt: data.twinKeyData.lastPlayedAt || null,
    };
  }

  // tradeDataの修復
  let repairedTradeData;
  if (!data.tradeData || typeof data.tradeData !== 'object') {
    repairedTradeData = createInitialTradeData();
  } else {
    const defaultTrade = createInitialTradeData();
    repairedTradeData = {
      ...defaultTrade,
      ...data.tradeData,
      zelvanOffers: data.tradeData.zelvanOffers || defaultTrade.zelvanOffers,
      reservedOffer: data.tradeData.reservedOffer || null,
      requestHistory: data.tradeData.requestHistory || defaultTrade.requestHistory,
      stats: { ...defaultTrade.stats, ...(data.tradeData.stats || {}) },
      upgradeHistory: Array.isArray(data.tradeData.upgradeHistory) ? data.tradeData.upgradeHistory : [],
      settings: { ...defaultTrade.settings, ...(data.tradeData.settings || {}) },
    };
  }

  return {
    gold: typeof data.gold === 'number' ? data.gold : defaults.gold,
    unopenedPacks: typeof data.unopenedPacks === 'number' ? data.unopenedPacks : 0,
    collection: Array.isArray(data.collection) ? data.collection : [],
    userDecks: Array.isArray(data.userDecks) ? data.userDecks : [],
    market: repairedMarket,
    assetHistory: repairedAssetHistory,
    merchantData: repairedMerchantData,
    tradeData: repairedTradeData,
    tournamentData: repairedTournamentData,
    investmentData: repairedInvestmentData,
    twinKeyData: repairedTwinKeyData,
    settings: repairedSettings,
    stats: {
      ...defaults.stats,
      ...(data.stats || {}),
    },
    createdAt: data.createdAt || Date.now(),
    updatedAt: Date.now(),
  };
};

// ========================================
// ユーザーデッキ操作
// ========================================

/**
 * 新しいユーザーデッキを作成
 * @param {string} name - デッキ名
 * @param {Array<{cardId: string, rarity: string}>} cards - カードリスト（40枚）
 * @returns {Object} デッキオブジェクト
 */
export const createUserDeck = (name, cards = []) => {
  return {
    id: `deck_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    name,
    cards,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
};

export default {
  DEFAULT_SETTINGS,
  STARTER_DECK_CARDS,
  getPlayerData,
  createInitialPlayerData,
  createInitialTournamentData,
  createInitialInvestmentData,
  createInitialTwinKeyData,
  validatePlayerData,
  repairPlayerData,
  createUserDeck,
};
